import type { Dashboard } from "@lib/types/dashboards";
import {
  DEFAULT_LAYOUT_ITEM,
  GRID_CONFIG,
  type Layout,
  type ResponsiveInsightLayouts,
} from "@lib/types/insights";

type LayoutSize = Pick<Layout, "w" | "h" | "minW" | "maxW" | "minH" | "maxH">;

function generateLayoutWithSize(
  insightId: string,
  existingLayouts: Layout[],
  cols: number,
  size: LayoutSize
): Layout {
  const width = Math.min(size.w, cols);
  const minW = size.minW ? Math.min(size.minW, width) : size.minW;
  const maxW = size.maxW ? Math.min(size.maxW, cols) : size.maxW;
  let maxY = 0;
  const occupiedPositions = new Set<string>();

  existingLayouts.forEach((item) => {
    for (let x = item.x; x < item.x + item.w; x++) {
      for (let y = item.y; y < item.y + item.h; y++) {
        occupiedPositions.add(`${x},${y}`);
      }
    }

    maxY = Math.max(maxY, item.y + item.h);
  });

  for (let y = 0; y <= maxY; y++) {
    for (let x = 0; x <= cols - 1; x++) {
      let canPlace = true;

      for (let dx = 0; dx < width && canPlace; dx++) {
        for (let dy = 0; dy < size.h && canPlace; dy++) {
          if (occupiedPositions.has(`${x + dx},${y + dy}`)) {
            canPlace = false;
          }
        }
      }

      if (canPlace && x + width <= cols) {
        return {
          i: insightId,
          x,
          y,
          w: width,
          h: size.h,
          minW,
          maxW,
          minH: size.minH,
          maxH: size.maxH,
        };
      }
    }
  }

  return {
    i: insightId,
    x: 0,
    y: maxY,
    w: width,
    h: size.h,
    minW,
    maxW,
    minH: size.minH,
    maxH: size.maxH,
  };
}

export function generateDashboardDefaultLayout(
  insightId: string,
  existingLayouts: Layout[],
  cols: number
): Layout {
  return generateLayoutWithSize(
    insightId,
    existingLayouts,
    cols,
    DEFAULT_LAYOUT_ITEM
  );
}

export function getDashboardInsightIds(tiles: Dashboard["tiles"]): string[] {
  return tiles.map((tile) => tile.insightId);
}

export function getDashboardLayouts(
  tiles: Dashboard["tiles"]
): ResponsiveInsightLayouts {
  return {
    lg: tiles.map((tile) => tile.layouts.lg),
    sm: tiles.map((tile) => tile.layouts.sm),
  };
}

export function mergeDashboardLayoutsIntoTiles(
  tiles: Dashboard["tiles"],
  newLayouts: ResponsiveInsightLayouts
): Dashboard["tiles"] {
  const lgMap = new Map(newLayouts.lg.map((layout) => [layout.i, layout]));
  const smMap = new Map(newLayouts.sm.map((layout) => [layout.i, layout]));

  return tiles.map((tile) => ({
    ...tile,
    layouts: {
      lg: lgMap.get(tile.insightId) ?? tile.layouts.lg,
      sm: smMap.get(tile.insightId) ?? tile.layouts.sm,
    },
  }));
}

export function generateDashboardResponsiveLayouts(
  insightIds: string[]
): ResponsiveInsightLayouts {
  const { cols } = GRID_CONFIG;

  const generateForBreakpoint = (colCount: number): Layout[] => {
    const layouts: Layout[] = [];
    const itemWidth = Math.min(DEFAULT_LAYOUT_ITEM.w, colCount);
    const itemsPerRow = Math.floor(colCount / itemWidth);

    insightIds.forEach((insightId, index) => {
      const col = index % itemsPerRow;
      const row = Math.floor(index / itemsPerRow);

      layouts.push({
        i: insightId,
        x: col * itemWidth,
        y: row * DEFAULT_LAYOUT_ITEM.h,
        w: itemWidth,
        h: DEFAULT_LAYOUT_ITEM.h,
        minW: Math.min(DEFAULT_LAYOUT_ITEM.minW ?? 1, colCount),
        maxW: colCount,
        minH: DEFAULT_LAYOUT_ITEM.minH,
        maxH: DEFAULT_LAYOUT_ITEM.maxH,
      });
    });

    return layouts;
  };

  return {
    lg: generateForBreakpoint(cols.lg),
    sm: generateForBreakpoint(cols.sm),
  };
}
